import {
  takeLatest, take, call, put, fork, cancel, select, cancelled, delay,
} from 'redux-saga/effects';
import cloneDeep from 'lodash/fp/cloneDeep';
import merge from 'lodash/merge';

import { FormState } from 'ia-react-core/~/interfaces/FormState';
import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';

import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';
import { Task } from 'redux-saga';
import ResetFormById from 'ia-react-core/~/actions/form/ResetFormById';
import validateFormById from 'ia-react-core/~/actions/form-fn/validateFormById';
import validateFormByName from 'ia-react-core/~/actions/form-fn/validateFormByName';
import reloadFormById from 'ia-react-core/~/actions/form-fn/reloadFormById';
import reloadFormByName from 'ia-react-core/~/actions/form-fn/reloadFormByName';
import getScoped from '../../../utils/redux/getScoped';
import coreLogger from '../../../utils/log/coreLogger';

import updateFormById from '../../../actions/form-fn/updateFormById';
import submitFormById from '../../../actions/form-fn/submitFormById';
import submitFormByName from '../../../actions/form-fn/submitFormByName';
import submitFormAcceptedById from '../../../actions/form-fn/submitFormAcceptedById';
import submitFormAcceptedByName from '../../../actions/form-fn/submitFormAcceptedByName';
import submitFormRejectedById from '../../../actions/form-fn/submitFormRejectedById';
import submitFormRejectedByName from '../../../actions/form-fn/submitFormRejectedByName';
import cancelSubmitFormById from '../../../actions/form-fn/cancelSubmitFormById';
import cancelSubmitFormByName from '../../../actions/form-fn/cancelSubmitFormByName';
import completeSubmitFormById from '../../../actions/form-fn/completeSubmitFormById';
import completeSubmitFormByName from '../../../actions/form-fn/completeSubmitFormByName';
import submitFormSuccessById from '../../../actions/form-fn/submitFormSuccessById';
import submitFormSuccessByName from '../../../actions/form-fn/submitFormSuccessByName';
import submitFormFailureById from '../../../actions/form-fn/submitFormFailureById';
import submitFormFailureByName from '../../../actions/form-fn/submitFormFailureByName';
import submitFormCompletedById from '../../../actions/form-fn/submitFormCompletedById';
import submitFormCompletedByName from '../../../actions/form-fn/submitFormCompletedByName';
import submitFormCanceledById from '../../../actions/form-fn/submitFormCanceledById';
import submitFormCanceledByName from '../../../actions/form-fn/submitFormCanceledByName';
import resetFormById from '../../../actions/form-fn/resetFormById';
import resetFormByName from '../../../actions/form-fn/resetFormByName';
import { FormComposedProps } from './Form';

type FormStateSelector<Values> = (state: DeepPartial<FormState>) => Values;
const makeStateSelector = <Values, >(path: string, scope: string[]) => (state: DeepPartial<FormState>): Values => getScoped(state, path, scope);

function getFormValueDst<Values>(props: FormComposedProps<Values>) {
  const { name, parentFormScope, ancestorValuesDst } = props;
  if (ancestorValuesDst) {
    const valuePath = parentFormScope.slice(1);
    return `${[ancestorValuesDst, ...valuePath, name].join('.').replace(/\.\[/g, '[')}`;
  }
  return props.valuesDst;
}

function* getFormValues<Values>(props: FormComposedProps<Values>) {
  const { scope } = props;
  const valuesDst: string = yield call(getFormValueDst as typeof getFormValueDst<Values>, props);
  const valueDstSelector: FormStateSelector<Values> = yield call(makeStateSelector as typeof makeStateSelector<Values>, valuesDst, scope);

  const values: Values = yield select(valueDstSelector);
  return values;
}

function* commitFormState<Values>(props: FormComposedProps<Values>, formStateChanges: DeepPartial<FormState>, mergeBlacklist?: object[]) {
  const { formId, ancestorFormId, formReducerKey, ancestorCommitFormState } = props;
  if (ancestorFormId) {
    const formStatePath = formReducerKey.split('.').slice(1).join('.');
    yield call(ancestorCommitFormState, formStateChanges, { formStatePath });
  } else {
    yield put(updateFormById(formId, formStateChanges, null, { mergeBlacklist }));
  }
}

function* validateFormSaga<Values extends object>(argsGetter: () => FormComposedProps<Values>): any {
  const props = argsGetter();
  const { validateForm, touchForm } = props;

  // We first touched to allow validation based on the touched status of other controls
  const touchedFormState = yield call(touchForm);
  yield call(commitFormState as typeof commitFormState<Values>, props, touchedFormState);
  yield delay(100);

  const formValues: Values = yield call(getFormValues as typeof getFormValues<Values>, props);
  const errorsFormState: ErrorMap = yield call(validateForm, formValues as object);

  yield call(commitFormState as typeof commitFormState<Values>, props, errorsFormState);

  return formValues;
}

function* cancellableFormSubmission<Values extends object>(argsGetter: () => FormComposedProps<Values>): any {
  const props = argsGetter();
  const {
    formId, name, formReducerKey, scope, setIsSubmitting, onSubmit, onValidationError, autoComplete, allowInvalid,
  } = props;

  const formStateSelector: FormStateSelector<Values> = yield call(makeStateSelector, formReducerKey, scope);

  let accepted;

  try {
    const formValues = yield validateFormSaga(argsGetter);

    const formState: FormState = yield select(formStateSelector);

    if (!allowInvalid && !formState.isDeeplyValid) {
      onValidationError?.(formValues, formState.errors);
      throw new Error('Deeply invalid form');
    }

    const submittingFormState: DeepPartial<FormState> = yield call(setIsSubmitting, true);
    yield call(commitFormState as typeof commitFormState<Values>, props, submittingFormState);

    accepted = true;

    const resolvedAutoComplete = (typeof autoComplete !== 'undefined') ? autoComplete : Boolean(onSubmit);

    let submitResult: boolean;
    if (onSubmit) {
      submitResult = yield call(onSubmit, formValues);
    }

    if (submitResult && submitResult !== true) {
      throw submitResult;
    } else if (!submitResult && resolvedAutoComplete !== true) {
      yield put(submitFormAcceptedById(formId));
      yield put(submitFormAcceptedByName(name));

      const { error } = yield take([
        `${completeSubmitFormById.type}/${formId}`,
        `${completeSubmitFormByName.type}/${name}`,
      ]);

      if (error) {
        throw error;
      }
    }

    yield put(submitFormSuccessById(formId));
    yield put(submitFormSuccessByName(name));
  } catch (error) {
    coreLogger.warn('Form: Error was thrown in saga.', error);

    if (!accepted) {
      yield put(submitFormRejectedById(formId));
      yield put(submitFormRejectedByName(name));
    } else {
      yield put(submitFormFailureById(formId));
      yield put(submitFormFailureByName(name));
    }
  } finally {
    const formState: DeepPartial<FormState> = yield select(formStateSelector);

    if (formState.isSubmitting) {
      const notSubmittingFormState: DeepPartial<FormState> = yield call(setIsSubmitting, false);
      yield call(commitFormState as typeof commitFormState<Values>, props, notSubmittingFormState);
    }

    if (yield cancelled()) {
      if (!accepted) {
        yield put(submitFormRejectedById(formId));
        yield put(submitFormRejectedByName(name));
      }

      yield put(submitFormCanceledById(formId));
      yield put(submitFormCanceledByName(name));
    }

    yield put(submitFormCompletedById(formId));
    yield put(submitFormCompletedByName(name));
  }
}

function* submitFormSaga<Values extends object>(argsGetter: () => FormComposedProps<Values>): any {
  const { formId, name } = argsGetter();

  const backgroundRequest: Task<any> = yield fork(cancellableFormSubmission as typeof cancellableFormSubmission<Values>, argsGetter);

  yield take([
    `${cancelSubmitFormById.type}/${formId}`,
    `${cancelSubmitFormByName.type}/${name}`,
  ]);

  yield cancel(backgroundRequest);
}

function* reloadFormSaga<Values extends object>(argsGetter: () => FormComposedProps<Values>): any {
  const props = argsGetter();
  const { formId, formReducerKey, scope, latestInitialValues } = props;
  // First we bump the version so the form updates its initial values
  const formStateSelector = yield call(makeStateSelector, formReducerKey, scope);
  const formState: DeepPartial<FormState> = yield select(formStateSelector);
  const { resetForm, setIsInitialized, setIsSubmitted } = props;
  const newState = merge(setIsSubmitted(false), setIsInitialized(false));
  yield call(commitFormState as typeof commitFormState<Values>, props, {
    ...newState,
    version: formState.version + 1,
  });

  // Second we update the form values. We do that first so that fields are shown/hidden according to new values
  yield put(updateFormById(formId, {}, latestInitialValues, { mergeBlacklist: [latestInitialValues] }));
  yield delay(100);

  // Third we reset the form state based on latest values
  const updatedFormState: DeepPartial<FormState> = yield select(formStateSelector);
  const clonedFormState: DeepPartial<FormState> = cloneDeep(updatedFormState);
  const formValues: Values = yield call(getFormValues as typeof getFormValues<Values>, props);
  const { formState: newFormState }: { values: object; formState: FormState } = yield call(resetForm, formValues, clonedFormState);
  const mergeBlacklist: object[] = [newFormState.clientErrors];

  // Finally the form is marked as being initialized.
  const nextState = merge(newFormState, setIsInitialized(true));
  yield call(commitFormState as typeof commitFormState<Values>, props, nextState, mergeBlacklist);
}

function* resetFormSaga<Values extends object>(argsGetter: () => FormComposedProps<Values>, { options: { resetValues = true, resetState = true } = {} }: ResetFormById): any {
  const props = argsGetter();
  const { formId, resetForm, initialValues, formReducerKey, scope, setIsInitialized, setIsSubmitted } = props;
  // First we reset the form values and state
  const formStateSelector = yield call(makeStateSelector, formReducerKey, scope);
  const formState: DeepPartial<FormState> = yield select(formStateSelector);
  const clonedFormState: DeepPartial<FormState> = cloneDeep(formState);
  const { values: newValues, formState: newFormState }: { values: object; formState: FormState } = yield call(resetForm, initialValues, clonedFormState, { resetValues, resetState });
  const newState = merge(setIsSubmitted(false), setIsInitialized(false));
  yield put(updateFormById(formId, newState, newValues, { mergeBlacklist: [newValues] }));
  // Finally the form is marked as being initialized.
  yield delay(0);
  const nextState = merge(newFormState, setIsInitialized(true));
  yield call(commitFormState as typeof commitFormState<Values>, props, {
    ...nextState,
    version: formState.version + 1,
  }, [nextState.cautions, nextState.clientErrors, nextState.dirty, nextState.errors, nextState.infos, nextState.touched, nextState.warnings]);
}

export default function* formSaga<Values extends object>(argsGetter: () => FormComposedProps<Values>) {
  const { formId, name } = argsGetter();

  yield takeLatest([
    `${submitFormById.type}/${formId}`,
    `${submitFormByName.type}/${name}`,
  ], submitFormSaga as typeof submitFormSaga<Values>, argsGetter);

  yield takeLatest([
    `${resetFormById.type}/${formId}`,
    `${resetFormByName.type}/${name}`,
  ], resetFormSaga as typeof submitFormSaga<Values>, argsGetter);

  yield takeLatest([
    `${reloadFormById.type}/${formId}`,
    `${reloadFormByName.type}/${name}`,
  ], reloadFormSaga as typeof reloadFormSaga<Values>, argsGetter);

  yield takeLatest([
    `${validateFormById.type}/${formId}`,
    `${validateFormByName.type}/${name}`,
  ], validateFormSaga as typeof validateFormSaga<Values>, argsGetter);
}
